import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Film, Image as ImageIcon, Type, Maximize2, Volume2 } from 'lucide-react';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const KIND_META = {
  video: {
    label: 'MinimaxHailuoVideo',
    icon: Film,
    accent: '#2DD4BF',
    header: 'VIDEO NODE',
  },
  image: {
    label: 'Image',
    icon: ImageIcon,
    accent: '#A78BFA',
    header: 'IMAGE NODE',
  },
  audio: {
    label: 'Audio',
    icon: Volume2,
    accent: '#FBBF24',
    header: 'AUDIO NODE',
  },
  text: {
    label: 'Text / Prompt',
    icon: Type,
    accent: '#93C5FD',
    header: 'TEXT NODE',
  },
  upscaler: {
    label: 'Upscaler',
    icon: Maximize2,
    accent: '#FBBF24',
    header: 'UPSCALER',
  },
  preview: {
    label: 'Preview',
    icon: TwinneeLogo,
    accent: '#F472B6',
    header: 'PREVIEW',
  },
};

function Port({ id, type, position, label, style }) {
  const isLeft = position === Position.Left;
  return (
    <>
      <Handle
        id={id}
        type={type}
        position={position}
        isConnectable
        isConnectableStart
        isConnectableEnd
        // Allow many edges from/to the same handle (fan-out / fan-in)
        className="!w-3.5 !h-3.5 !border-2 !border-[#0b1015] !bg-[#62FAE3] !z-20 hover:!bg-[#a7f3e8] transition-colors"
        style={style}
        title={label ? `${type}: ${label}` : undefined}
      />
      {label ? (
        <span
          className={`pointer-events-none absolute z-10 text-[9px] text-slate-400 font-mono ${
            isLeft ? 'left-3' : 'right-3'
          }`}
          style={style}
        >
          {label}
        </span>
      ) : null}
    </>
  );
}

function WorkflowNodeComponent({ data, selected }) {
  const kind = data?.kind || 'text';
  const meta = KIND_META[kind] || KIND_META.text;
  const Icon = meta.icon;
  const title = data?.label || meta.label;
  const isActiveVersion = Boolean(data?.isActive && data?.mediaVersion);
  const isInactiveVersion = Boolean(data?.mediaVersion && !data?.isActive);

  return (
    <div
      className={`w-[240px] rounded-xl border bg-[#0f1418]/95 shadow-xl backdrop-blur-md transition-all ${
        isActiveVersion
          ? 'ring-2 ring-[#62FAE3] border-[#62FAE3]/80 shadow-[0_0_28px_rgba(98,250,227,0.25)]'
          : selected
            ? 'ring-2 ring-[#62FAE3]/60 border-[#62FAE3]/50'
            : isInactiveVersion
              ? 'border-white/10 opacity-75'
              : 'border-white/10'
      }`}
      style={{ boxShadow: isActiveVersion || selected ? `0 0 24px ${meta.accent}33` : undefined }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-t-xl border-b border-white/10"
        style={{ background: `${meta.accent}18` }}
      >
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${meta.accent}33`, color: meta.accent }}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] font-mono tracking-widest text-slate-400 uppercase">{meta.header}</p>
          <p className="text-xs font-bold text-white truncate">{title}</p>
        </div>
        {data?.mediaVersion ? (
          <span
            className={`shrink-0 text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md ${
              isActiveVersion
                ? 'bg-[#62FAE3] text-[#041016]'
                : 'bg-white/10 text-slate-400'
            }`}
          >
            {isActiveVersion ? 'Selected' : 'Alt'}
          </span>
        ) : null}
      </div>

      <div className="relative px-3 py-3 space-y-2 min-h-[72px]">
        {kind === 'video' && (
          <>
            <Port id="prompt" type="target" position={Position.Left} label="prompt" style={{ top: 18 }} />
            <Port id="image" type="target" position={Position.Left} label="image" style={{ top: 42 }} />
            {data?.imageUrl || data?.videoUrl ? (
              <div className="ml-10 mr-10 rounded-lg overflow-hidden border border-white/10 h-[72px] bg-black/40 relative">
                {data.imageUrl ? (
                  <img src={data.imageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-[#2DD4BF] font-mono">
                    VIDEO
                  </div>
                )}
                {data.videoUrl ? (
                  <span className="absolute bottom-1 right-1 text-[8px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-[#2DD4BF]">
                    v{data.versionIndex || ''}/{data.versionTotal || ''}
                  </span>
                ) : null}
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 pl-10 pr-10 leading-relaxed line-clamp-2">
                No video version yet
              </p>
            )}
            <div className="pl-10 pr-10 text-[9px] font-mono text-slate-500 truncate">
              {isActiveVersion ? 'active in scene' : data?.text || 'alternate version'}
            </div>
            <Port id="video" type="source" position={Position.Right} label="video" style={{ top: 30 }} />
          </>
        )}

        {kind === 'image' && (
          <>
            <Port id="prompt" type="target" position={Position.Left} label="prompt" style={{ top: 24 }} />
            {data?.imageUrl ? (
              <div className="ml-8 mr-8 rounded-lg overflow-hidden border border-white/10 h-[72px] bg-black/40 relative">
                <img src={data.imageUrl} alt="" className="w-full h-full object-cover" />
                {data.mediaVersion ? (
                  <span className="absolute bottom-1 right-1 text-[8px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-[#A78BFA]">
                    v{data.versionIndex}/{data.versionTotal}
                  </span>
                ) : null}
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 pl-10 pr-10 line-clamp-2">
                {data?.text || 'No image version yet'}
              </p>
            )}
            <p className="pl-10 text-[9px] font-mono text-slate-500 truncate">
              {isActiveVersion ? 'active in scene' : data?.mediaVersion ? 'click to select' : 'synced'}
            </p>
            <Port id="image" type="source" position={Position.Right} label="image" style={{ top: 24 }} />
          </>
        )}

        {kind === 'audio' && (
          <>
            <Port id="in" type="target" position={Position.Left} label="in" style={{ top: 24 }} />
            <div className="ml-8 mr-8 h-[72px] rounded-lg border border-white/10 bg-black/40 flex flex-col items-center justify-center gap-1 relative">
              <Volume2 className="w-5 h-5 text-[#FBBF24]" />
              <span className="text-[10px] font-mono text-slate-400">
                {data?.audioUrl ? 'Audio take' : 'No audio yet'}
              </span>
              {data.mediaVersion ? (
                <span className="absolute bottom-1 right-1 text-[8px] font-mono px-1.5 py-0.5 rounded bg-black/70 text-[#FBBF24]">
                  v{data.versionIndex}/{data.versionTotal}
                </span>
              ) : null}
            </div>
            <p className="pl-10 text-[9px] font-mono text-slate-500 truncate">
              {isActiveVersion ? 'active in scene' : data?.mediaVersion ? 'click to select' : ''}
            </p>
            <Port id="audio" type="source" position={Position.Right} label="audio" style={{ top: 24 }} />
          </>
        )}

        {kind === 'text' && (
          <>
            <Port id="in" type="target" position={Position.Left} label="in" style={{ top: 28 }} />
            <p className="text-[10px] text-slate-300 leading-relaxed line-clamp-3 whitespace-pre-wrap pl-8">
              {data?.text || 'Enter prompt text…'}
            </p>
            {data?.synced ? (
              <p className="text-[9px] font-mono text-[#62FAE3]/80 pl-8">
                synced · {data.field === 'title' ? 'story' : 'scene script'}
              </p>
            ) : null}
            <Port id="text" type="source" position={Position.Right} label="text" style={{ top: 28 }} />
          </>
        )}

        {kind === 'upscaler' && (
          <>
            <Port id="image_in" type="target" position={Position.Left} label="image" style={{ top: 18 }} />
            <Port id="video_in" type="target" position={Position.Left} label="video" style={{ top: 42 }} />
            <p className="text-[10px] text-slate-400 pl-10 pr-10">Upscale image or video (2× / 4×).</p>
            <Port id="out" type="source" position={Position.Right} label="out" style={{ top: 30 }} />
          </>
        )}

        {kind === 'preview' && (
          <>
            <Port id="in" type="target" position={Position.Left} label="in" style={{ top: 28 }} />
            <div className="ml-8 h-16 rounded-lg border border-dashed border-white/15 bg-white/[0.03] flex items-center justify-center text-[10px] text-slate-500">
              Preview output
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const WorkflowNode = memo(WorkflowNodeComponent);

export const WORKFLOW_NODE_TYPES = {
  workflowNode: WorkflowNode,
};

export const WORKFLOW_PALETTE = [
  {
    kind: 'video',
    label: 'MinimaxHailuoVideoNode',
    description: 'Generate video from prompt + image',
    accent: '#2DD4BF',
    Icon: Film,
  },
  {
    kind: 'image',
    label: 'ImageNode',
    description: 'Image / reference frame',
    accent: '#A78BFA',
    Icon: ImageIcon,
  },
  {
    kind: 'audio',
    label: 'AudioNode',
    description: 'Narration / dialogue audio',
    accent: '#FBBF24',
    Icon: Volume2,
  },
  {
    kind: 'text',
    label: 'TextPromptNode',
    description: 'Prompt or script text',
    accent: '#93C5FD',
    Icon: Type,
  },
  {
    kind: 'upscaler',
    label: 'UpscalerNode',
    description: 'Upscale image or video',
    accent: '#FBBF24',
    Icon: Maximize2,
  },
  {
    kind: 'preview',
    label: 'PreviewNode',
    description: 'Inspect connected output',
    accent: '#F472B6',
    Icon: TwinneeLogo,
  },
];

export function createWorkflowNode(kind, position, overrides = {}) {
  const meta = WORKFLOW_PALETTE.find((p) => p.kind === kind) || WORKFLOW_PALETTE[2];
  const id = `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  return {
    id,
    type: 'workflowNode',
    position: position || { x: 120, y: 120 },
    data: {
      kind,
      label: overrides.label || meta.label,
      text: overrides.text || (kind === 'text' ? 'A cinematic shot of a fox in misty woods' : ''),
      duration: overrides.duration || '6s',
    },
  };
}

export function defaultWorkflowGraph() {
  const text = createWorkflowNode('text', { x: 80, y: 160 });
  const image = createWorkflowNode('image', { x: 80, y: 340 });
  const video = createWorkflowNode('video', { x: 420, y: 220 });
  const upscaler = createWorkflowNode('upscaler', { x: 760, y: 220 });
  const preview = createWorkflowNode('preview', { x: 1080, y: 220 });
  return {
    nodes: [text, image, video, upscaler, preview],
    edges: [
      {
        id: `e_${text.id}_${video.id}_prompt`,
        source: text.id,
        sourceHandle: 'text',
        target: video.id,
        targetHandle: 'prompt',
        animated: true,
        style: { stroke: '#62FAE3', strokeWidth: 1.5 },
      },
      {
        id: `e_${image.id}_${video.id}_image`,
        source: image.id,
        sourceHandle: 'image',
        target: video.id,
        targetHandle: 'image',
        animated: true,
        style: { stroke: '#A78BFA', strokeWidth: 1.5 },
      },
      {
        id: `e_${video.id}_${upscaler.id}`,
        source: video.id,
        sourceHandle: 'video',
        target: upscaler.id,
        targetHandle: 'video_in',
        animated: true,
        style: { stroke: '#2DD4BF', strokeWidth: 1.5 },
      },
      {
        id: `e_${upscaler.id}_${preview.id}`,
        source: upscaler.id,
        sourceHandle: 'out',
        target: preview.id,
        targetHandle: 'in',
        animated: true,
        style: { stroke: '#FBBF24', strokeWidth: 1.5 },
      },
    ],
  };
}
